import React, { useState } from 'react';
import { CommitmentData } from '../../types';
import {
  Card,
  CardContent,
  Divider,
  Skeleton,
  styled,
  Tooltip,
  Typography,
  Box,
} from '@mui/material';
import { UpdateCommitment } from '../../useCommitmentView';
import Row from 'components/Layout/Row';
import { Column } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import {
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import { EditableField } from './EditableField';
import EditableFieldsRow, { OutputData } from './EditableFieldsRow';
import { RolePermission } from 'further-types/admin-user-role';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { CommitmentType } from 'further-types/lp/vehicle';
import TooltipTriggerText from 'components/TooltipTriggerText';

const InlineTextSkeleton = styled(Skeleton)({
  display: 'inline-block',
  width: 60,
});

const TooltipTriggerTextWrapper = styled(Box)({
  maxWidth: '100px',
});

type Props = Pick<
  CommitmentData,
  'overview' | 'vehicleId' | 'currencySettings' | 'investorName'
> & {
  onChange: UpdateCommitment;
  isCommitmentAggregate?: boolean;
  calculatingCurrencyConversions?: boolean;
  showInvestorConvertedCurrency?: boolean;
};

const Overview: React.FC<Props> = ({
  overview,
  currencySettings,
  vehicleId,
  investorName,
  calculatingCurrencyConversions,
  isCommitmentAggregate,
  showInvestorConvertedCurrency,
  onChange,
}) => {
  const [editedField, setEditedField] = useState<EditableField | null>(null);
  const [canEditInvestor, canEditCommitment] = useHasPermissions([
    RolePermission.EditInvestor,
    RolePermission.LpEditCommitment,
  ]);

  const { vehicle } = useVehicle(vehicleId);
  const isCapitalAndLoan =
    vehicle?.commitmentType === CommitmentType.CapitalAndLoan;

  const {
    currencyLcy,
    investorCurrency,
    currencyFeedService,
    exchangeRateLcyToInvestor,
  } = currencySettings;

  const showTotalCommitmentTooltip =
    isCapitalAndLoan &&
    overview.capitalCommitmentLcy !== undefined &&
    overview.loanCommitmentLcy !== undefined &&
    (overview.capitalCommitmentLcy > 0 || overview.loanCommitmentLcy > 0);

  const totalCommitmentTooltipContent = showTotalCommitmentTooltip ? (
    <>
      <Typography variant="body2" color="white">
        Capital contribution:{' '}
        {numberToCurrencyString(overview.capitalCommitmentLcy ?? 0, {
          currency: currencyLcy,
        })}
      </Typography>
      <Typography variant="body2" color="white">
        Loan commitment:{' '}
        {numberToCurrencyString(overview.loanCommitmentLcy ?? 0, {
          currency: currencyLcy,
        })}
      </Typography>
    </>
  ) : null;

  const advanceSubscriptionBalanceLcy =
    overview.advanceSubscriptionBalanceLcy ?? 0;
  const showFundedCommitmentTooltip = advanceSubscriptionBalanceLcy > 0;
  const fundedCommitmentTooltipContent = showFundedCommitmentTooltip ? (
    <Typography variant="body2" color="white">
      In addition to the funded commitment, {investorName} has a current advance
      subscription balance of{' '}
      {numberToCurrencyString(advanceSubscriptionBalanceLcy, {
        currency: currencyLcy,
      })}
      .
    </Typography>
  ) : null;

  const readOnlyFields = [
    {
      label: 'Total commitment',
      ...overview.totalCommitment,
      showTooltip: showTotalCommitmentTooltip,
      tooltipContent: totalCommitmentTooltipContent,
    },
    {
      label: 'Funded commitment',
      ...overview.fundedCommitment,
      showTooltip: showFundedCommitmentTooltip,
      tooltipContent: fundedCommitmentTooltipContent,
    },
    {
      label: 'Unfunded commitment (incl. recalls)',
      ...overview.unfundedCommitment,
      showTooltip: false,
      tooltipContent: undefined,
    },
    {
      label: 'Live capital on account',
      ...overview.liveCapitalOnAccount,
      showTooltip: false,
      tooltipContent: undefined,
    },
  ];

  const editableFields = [
    {
      label: 'Investor currency',
      value: investorCurrency,
      key: EditableField.InvestorCurrency,
      hasEditPermission: canEditInvestor,
    },
    {
      label: 'Management fee template',
      value: overview.feeTemplate?.name,
      key: EditableField.FeeTemplateId,
      hasEditPermission: canEditCommitment,
    },
    {
      label: 'Equalisation interest fee template',
      value: overview.equalisationInterestFeeTemplate?.name,
      key: EditableField.EqualisationInterestFeeTemplateId,
      hasEditPermission: canEditCommitment,
    },
    {
      label: 'Carried interest fee template',
      value: overview.carriedInterestFeeTemplate?.name,
      key: EditableField.CarriedInterestFeeTemplateId,
      hasEditPermission: canEditCommitment,
    },
  ];

  const commitmentAggregateEnabledFields = [EditableField.InvestorCurrency];

  const handleSave = (data: OutputData, key: EditableField) => {
    onChange({
      ...(key === EditableField.FeeTemplateId && {
        feeTemplate: data.feeTemplate,
      }),
      ...(key === EditableField.CarriedInterestFeeTemplateId && {
        carriedInterestFeeTemplate: data.carriedInterestFeeTemplate,
      }),
      ...(key === EditableField.EqualisationInterestFeeTemplateId && {
        equalisationInterestFeeTemplate: data.equalisationInterestFeeTemplate,
      }),
      ...(key === EditableField.InvestorCurrency && {
        investorCurrency: data.investorCurrency,
      }),
    });
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Column spacing="lg">
          <Row spacing="lg">
            {readOnlyFields.map(
              ({
                label,
                valueTcy,
                valueLcy,
                percentageOfTotal,
                showTooltip = false,
                tooltipContent,
              }) => {
                const commitmentAmount = numberToCurrencyString(valueLcy, {
                  currency: currencyLcy,
                });

                const commitmentValue =
                  showTooltip && tooltipContent ? (
                    <Tooltip title={tooltipContent} placement="top">
                      <TooltipTriggerTextWrapper>
                        <TooltipTriggerText
                          content={commitmentAmount}
                          noMarginTop
                        />
                      </TooltipTriggerTextWrapper>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="grey.900">
                      {commitmentAmount}
                    </Typography>
                  );

                return (
                  <FormField key={label} label={label} spacing="xs">
                    <Column spacing="xs">
                      {commitmentValue}
                      <Typography variant="body2">
                        {percentageOfTotal !== undefined ? (
                          <>
                            (
                            {numberToPercentageString(percentageOfTotal, {
                              accuracy: 2,
                            })}
                            ){' '}
                          </>
                        ) : null}
                        {showInvestorConvertedCurrency ? (
                          calculatingCurrencyConversions ? (
                            <InlineTextSkeleton />
                          ) : (
                            numberToCurrencyString(valueTcy, {
                              currency: investorCurrency,
                            })
                          )
                        ) : null}
                      </Typography>
                    </Column>
                  </FormField>
                );
              },
            )}
          </Row>
          <Divider />
          <Column spacing="xl">
            <Row spacing="lg">
              {editableFields.map((data) => {
                const { label, value, key, hasEditPermission } = data;
                if (editedField && editedField !== key) return null;
                const isEditingField = editedField === key;
                const editingEnabledInThisTab = isCommitmentAggregate
                  ? commitmentAggregateEnabledFields.includes(key)
                  : true;
                return (
                  <FormField
                    editable={
                      hasEditPermission &&
                      editingEnabledInThisTab &&
                      !isEditingField
                    }
                    onEdit={() => setEditedField(key)}
                    key={key}
                    label={label}
                    spacing="xs"
                  >
                    {isEditingField ? (
                      <EditableFieldsRow
                        onSave={(data) => {
                          handleSave(data, key);
                        }}
                        editedField={editedField}
                        onClose={() => setEditedField(null)}
                        vehicleId={vehicleId}
                        investorCurrency={currencySettings.investorCurrency}
                        feeTemplateId={overview.feeTemplate?.id || ''}
                        carriedInterestFeeTemplateId={
                          overview.carriedInterestFeeTemplate?.id || ''
                        }
                        equalisationInterestFeeTemplateId={
                          overview.equalisationInterestFeeTemplate?.id || ''
                        }
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        color={value ? 'grey.900' : undefined}
                      >
                        {editingEnabledInThisTab ? (
                          value || '--'
                        ) : (
                          <i>See individual commitments</i>
                        )}
                      </Typography>
                    )}
                  </FormField>
                );
              })}
            </Row>
            <Column spacing="xs">
              <Typography variant="caption">
                {currencyLcy} 1.00 = {investorCurrency}{' '}
                {exchangeRateLcyToInvestor.toFixed(2)} at 00:00 today (Feed:{' '}
                {currencyFeedService})
              </Typography>
              <Typography variant="caption">
                Holding value {currencyLcy} figures are live converted from the
                relevant transaction currency. Provided for guidance purposes
                only.
              </Typography>
            </Column>
          </Column>
        </Column>
      </CardContent>
    </Card>
  );
};

export default Overview;
